// Phase 7.26: API base URL is env-driven (CRA injects REACT_APP_* at build
// time). Default is the test API; production builds must set
// REACT_APP_API_BASE_URL=https://api.kayfa.io/v1 explicitly. For local
// testing, set
//   REACT_APP_API_BASE_URL=http://localhost:3001/v1
// in kayfa-admin/.env.local (gitignored) and restart the dev server — no need
// to edit this file anymore. See .env.example.
// Runtime-first: read the injected global (window.__RUNTIME_CONFIG__, set by
// public/env-config.js and regenerated per-environment at container start) so
// one built image serves test/pre-prod/prod. Falls back to the build-time env
// (local dev via .env.local) and finally a safe default.
const baseUrl =
  (typeof window !== 'undefined' &&
    window.__RUNTIME_CONFIG__?.REACT_APP_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://test-api.kayfa.io/v1';

// Query-string builder for the endpoints that take many optional filters.
// URLSearchParams does the percent-encoding, so a batchName containing spaces
// (they all do) is safe without a manual encodeURIComponent at every call site.
// Empty strings, null/undefined and `false` are dropped rather than sent as
// "false" — a filter the user has not set must not reach the BE at all.
const queryString = (q: Record<string, string | number | boolean | undefined | null>) =>
  new URLSearchParams(
    Object.entries(q)
      .filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false)
      .map(([k, v]) => [k, String(v)]),
  ).toString();

// Same builder, but it drops the '?' entirely when every filter is empty. That
// matters for the endpoints whose filters are ALL optional (E6's admin list and
// health): a bare trailing '?' is legal but it turns up verbatim in logs and in
// support screenshots, and "GET /recordings/admin?" reads like a truncated URL.
const withQuery = (
  url: string,
  q: Record<string, string | number | boolean | undefined | null>,
) => {
  const s = queryString(q);
  return s ? `${url}?${s}` : url;
};

// Lead-forms filters. Unlike queryString(), a boolean `false` is a real filter
// there (funnel `metaClick=false`, leads `hasPhone=false`), so booleans are
// sent as 'true' / 'false'; an unset filter is undefined, null or ''.
export type LeadFormsQuery = Record<string, string | number | boolean | undefined | null>;
const leadFormsQuery = (q: LeadFormsQuery) => {
  const out: Record<string, string | number | undefined | null> = {};
  Object.keys(q).forEach((k) => {
    const v = q[k];
    out[k] = typeof v === 'boolean' ? String(v) : v;
  });
  return out;
};

export const APIS = {
  AUTH: {
    SIGNIN: `${baseUrl}/auth/sign-in`,
    // Staff sign-in, step 2: the emailed / WhatsApp one-time code opens the session.
    VERIFY_OTP: `${baseUrl}/auth/verify-otp/`,
    SIGNUP: `${baseUrl}/auth/sign-up`,
    ME: `${baseUrl}/auth/me`,
    // Phase 2.1: refresh-token + change-password + sessions endpoints.
    REFRESH: `${baseUrl}/auth/refresh-token`,
    CHANGE_PASSWORD: `${baseUrl}/auth/change-password`,
    LOGOUT_BY_TOKEN: (token: string) => `${baseUrl}/auth/logout/${token}`,
    SESSIONS: `${baseUrl}/auth/sessions`,
    SESSION_BY_ID: (id: string) => `${baseUrl}/auth/sessions/${id}`,
    // Phase 7.14: revoke the caller's CURRENT session (logout this device).
    SESSION_CURRENT: `${baseUrl}/auth/sessions/current`,
    // Phase 4b.3: MFA (TOTP) enrolment endpoints.
    MFA_SETUP: `${baseUrl}/auth/mfa/setup`,
    MFA_VERIFY: `${baseUrl}/auth/mfa/verify`,
    MFA_DISABLE: `${baseUrl}/auth/mfa/disable`,
  },
  // Phase 3: super-admin only management endpoints.
  ADMINS: {
    LIST: `${baseUrl}/admins`,
    BY_ID: (id: string) => `${baseUrl}/admins/${id}`,
    SESSIONS: (id: string) => `${baseUrl}/admins/${id}/sessions`,
    SESSION_BY_ID: (uid: string, sid: string) =>
      `${baseUrl}/admins/${uid}/sessions/${sid}`,
    RESET_PASSWORD: (id: string) => `${baseUrl}/admins/${id}/reset-password`,
    // Phase 7.10: admin-action audit log (super-admin).
    AUDIT: `${baseUrl}/admins/audit`,
  },
  // Group B (analytics): admin KPI stats, certificate listings, payments.
  ADMIN_STATS: {
    OVERVIEW: `${baseUrl}/admin/stats/overview`,
    SIGNUPS: `${baseUrl}/admin/stats/signups`,
    REVENUE: `${baseUrl}/admin/stats/revenue`,
    ACTIVE_SUBSCRIPTIONS: `${baseUrl}/admin/stats/active-subscriptions`,
    CHURN: `${baseUrl}/admin/stats/churn`,
    TOP_PACKAGES: `${baseUrl}/admin/stats/top-packages`,
    // Revenue/subscription analytics report.
    REVENUE_TIMESERIES: `${baseUrl}/admin/stats/revenue-timeseries`,
    SUBSCRIPTIONS: `${baseUrl}/admin/stats/subscriptions`,
    // Phase 8.x: full customer-care overview for one student.
    USER_OVERVIEW: (id: string) => `${baseUrl}/admin/stats/user-overview/${id}`,
    // Phase 8.x: a student's progress in ONE course (drill-down).
    USER_COURSE_PROGRESS: (userId: string, courseId: string) =>
      `${baseUrl}/admin/stats/user-course/${userId}/${courseId}`,
    // Phase 8.x (powerbi-charts): quiz-score distribution + activity heatmap.
    SCORE_DISTRIBUTION: `${baseUrl}/admin/stats/score-distribution`,
    ACTIVITY_HEATMAP: `${baseUrl}/admin/stats/activity-heatmap`,
    // Phase: user segmentation analytics.
    SEGMENTS: `${baseUrl}/admin/stats/segments`,
    UPCOMING_EXPIRATIONS: `${baseUrl}/admin/stats/upcoming-expirations`,
    // Per-course report (enrolled/completion/quiz/watch/rating/certificates).
    COURSE_REPORT: (courseId: string) =>
      `${baseUrl}/admin/stats/course-report/${courseId}`,
    // All students in a course + their per-student completion scores.
    COURSE_STUDENTS: (courseId: string) =>
      `${baseUrl}/admin/stats/course-students/${courseId}`,
    // All course ratings + comments (admin Course Ratings view).
    COURSE_RATINGS: `${baseUrl}/admin/stats/course-ratings`,
    // Per-diploma (bootcamp) report + its enrolled-student roster.
    DIPLOMA_REPORT: (diplomaId: string) =>
      `${baseUrl}/admin/stats/diploma-report/${diplomaId}`,
    DIPLOMA_STUDENTS: (diplomaId: string) =>
      `${baseUrl}/admin/stats/diploma-students/${diplomaId}`,
  },
  ADMIN_CERTIFICATES: {
    COURSE: `${baseUrl}/admin/certificates`,
    TRACK: `${baseUrl}/admin/track-certificates`,
    // POST {reason}: flags the certificate revoked (kept, never deleted).
    // @Auth(ADMIN, SUPER_ADMIN).
    REVOKE_COURSE: (id: string) => `${baseUrl}/admin/certificates/${id}/revoke`,
    REVOKE_TRACK: (id: string) => `${baseUrl}/admin/track-certificates/${id}/revoke`,
  },
  // Phase 8.x: final projects (capstone definitions + student submissions + grading).
  FINAL_PROJECTS: {
    LIST: `${baseUrl}/final-projects`,
    DEFINE: `${baseUrl}/final-projects`,
    PATCH: (id: string) => `${baseUrl}/final-projects/${id}`,
    BY_COURSE: (courseId: string) => `${baseUrl}/final-projects/course/${courseId}`,
    PROJECT_SUBMISSIONS: (id: string) => `${baseUrl}/final-projects/${id}/submissions`,
    ALL_SUBMISSIONS: `${baseUrl}/final-projects/submissions`,
    GRADE: (submissionId: string) =>
      `${baseUrl}/final-projects/submissions/${submissionId}/grade`,
    STUDENT_SUBMISSIONS: (userId: string) =>
      `${baseUrl}/final-projects/submissions/student/${userId}`,
  },
  // Phase 8.x: instructor evaluations (student ratings).
  INSTRUCTOR_EVALUATIONS: {
    LIST: `${baseUrl}/instructor-evaluations`,
    STATS: `${baseUrl}/instructor-evaluations/stats`,
  },
  // Phase 8.x: smart alerts / student classification.
  STUDENT_ALERTS: {
    LIST: `${baseUrl}/admin/alerts`,
    SUMMARY: `${baseUrl}/admin/alerts/summary`,
    RECOMPUTE: `${baseUrl}/admin/alerts/recompute`,
  },
  // Phase 7.2: quiz attempts (admin listing of graded submissions).
  QUIZ_ATTEMPTS: {
    ALL: `${baseUrl}/quiz-attempts`,
    BY_USER: (id: string) => `${baseUrl}/quiz-attempts/user/${id}`,
    // Phase 8.x: per-quiz aggregation report.
    STATS: `${baseUrl}/quiz-attempts/stats`,
    // #2: per-student-per-quiz rollup (final score = average across attempts).
    BY_STUDENT: `${baseUrl}/quiz-attempts/by-student`,
    // Per-batch (cohort) rollup for ONE program — drives the batch comparison
    // and the dedicated per-batch report.
    BATCH_COMPARISON: `${baseUrl}/quiz-attempts/batch-comparison`,
    // Item analysis for one quiz: per-question accuracy, skips, option
    // distribution and discrimination.
    QUESTION_ANALYTICS: `${baseUrl}/quiz-attempts/question-analytics`,
    // Per-student answers for one quiz (student x question grid).
    STUDENT_ANSWERS: `${baseUrl}/quiz-attempts/student-answers`,
  },
  PAYMENTS: {
    LIST: `${baseUrl}/payments`,
    EXPORT_CSV: `${baseUrl}/payments/export.csv`,
    // Ask Paymob what really happened to an order (missed webhook, refund).
    RECONCILE: (reference: string) =>
      `${baseUrl}/payments/${encodeURIComponent(reference)}/reconcile`,
  },
  // Phase 6.8: customer-care impersonation endpoints. SUPPORT role
  // performs START / END / HISTORY against their own work; SUPER_ADMIN
  // sees ACTIVE / HISTORY_BY_TARGET and force-revokes via BY_JTI.
  IMPERSONATION: {
    START: `${baseUrl}/admin/impersonation`,
    END_CURRENT: `${baseUrl}/admin/impersonation/current`,
    HISTORY: `${baseUrl}/admin/impersonation/history`,
    HISTORY_BY_TARGET: (email: string) =>
      `${baseUrl}/admin/impersonation/history/target?email=${encodeURIComponent(email)}`,
    ACTIVE: `${baseUrl}/admin/impersonation/active`,
    BY_JTI: (jti: string) =>
      `${baseUrl}/admin/impersonation/${encodeURIComponent(jti)}`,
  },
  CONTACT: {
    BY_ID: (id: string) => `${baseUrl}/contact-us/${id}`,
    LIST: `${baseUrl}/contact-us`,
  },
  USERS: {
    LIST: `${baseUrl}/users`,
    // FE.4 (client-side-pagination-list-pages): server-side paginated list.
    // Returns {total,page,pageSize,items}; query params page/pageSize/search.
    PAGINATED: `${baseUrl}/users/list`,
    ADD: `${baseUrl}/users/create`,
    BYId: (id: string) => `${baseUrl}/users/${id}`,
    // Group B (7.18): streaming CSV export of users.
    EXPORT_CSV: `${baseUrl}/users/export.csv`,
    // 7.24: a user's enrolled courses (admin/support).
    COURSES: (id: string) => `${baseUrl}/users/${id}/courses`,
    // Phase: user segmentation — admin-only set source/free-access/batch.
    SEGMENT: (id: string) => `${baseUrl}/users/${id}/segment`,
    // Diploma-only edit — the single write customer-care has on a student.
    // Deliberately separate from SEGMENT, which carries freeAccess.
    DIPLOMAS: (id: string) => `${baseUrl}/users/${id}/diplomas`,
    // Distinct diploma batch labels (add-user dropdown + Students filter).
    BATCHES: `${baseUrl}/users/batches`,
  },
  INSTRUCTORS: {
    LIST: `${baseUrl}/instructors`,
    ADD: `${baseUrl}/instructors/create`,
    PATCH: (id: string) => `${baseUrl}/instructors/${id}`,
  },
  // Access grants (owner decisions 2026-09-23): one row per purchase or staff
  // grant, with its own start/end. GET is readable by support/sales; POST and
  // revoke need admin, super-admin or sales-manager.
  ENTITLEMENTS: {
    BY_USER: (userId: string) => `${baseUrl}/entitlements/user/${userId}`,
    CREATE: `${baseUrl}/entitlements`,
    REVOKE: (id: string) => `${baseUrl}/entitlements/${id}/revoke`,
  },
  PACKAGES: {
    LIST: `${baseUrl}/packages`,
    ADD: `${baseUrl}/packages/create`,
    PATCH: (id: string) => `${baseUrl}/packages/${id}`,
    ACTIVE: `${baseUrl}/packages/active`,
  },
  PROMO_CODE: {
    LIST: `${baseUrl}/promoCodes`,
    ADD: `${baseUrl}/promoCodes/create`,
    PATCH: (id: string) => `${baseUrl}/promoCodes/${id}`,
    BY_ID: (id: string) => `${baseUrl}/promoCodes/${id}`,
    // Phase 7.21: wire the (previously no-op) delete handler. In prod baseUrl
    // is http://localhost:3001/v1 so these URLs are unchanged; using baseUrl
    // just lets promo-code calls follow the same toggle as the rest of the app.
    DELETE: (id: string) => `${baseUrl}/promoCodes/${id}`,
  },
  CATEGORIES: {
    LIST: `${baseUrl}/categories`,
    // client-side-pagination-list-pages: server-paginated {total,items} list.
    PAGINATED: `${baseUrl}/categories/list`,
    ADD: `${baseUrl}/categories/create`,
    PATCH: (id: string) => `${baseUrl}/categories/${id}`,
    ACTIVE: `${baseUrl}/categories/active`,
  },
  DIPLOMAS: {
    LIST: `${baseUrl}/diplomas`,
    ACTIVE: `${baseUrl}/diplomas/active`,
    ENROLLMENT_COUNTS: `${baseUrl}/diplomas/enrollment-counts`,
    ADD: `${baseUrl}/diplomas/create`,
    PATCH: (id: string) => `${baseUrl}/diplomas/${id}`,
    DELETE: (id: string) => `${baseUrl}/diplomas/${id}`,
    BY_ID: (id: string) => `${baseUrl}/diplomas/${id}`,
    // Recorded-video progress: real watch-% per student × video, scoped to a
    // track (+ optional batch). MATRIX returns JSON; EXPORT_CSV downloads it.
    RECORDED_MATRIX: (id: string, trackId?: string, batchName?: string) =>
      `${baseUrl}/diplomas/${id}/recorded-matrix?trackId=${encodeURIComponent(
        trackId || '',
      )}&batchName=${encodeURIComponent(batchName || '')}`,
    RECORDED_EXPORT_CSV: (id: string, trackId?: string, batchName?: string) =>
      `${baseUrl}/diplomas/${id}/recorded-export.csv?trackId=${encodeURIComponent(
        trackId || '',
      )}&batchName=${encodeURIComponent(batchName || '')}`,
    // Landing page editor (SPEC §C.5–C.9). Landing roles: admin, customer-care,
    // sales, sales-manager. LANDING is GET (editor payload) and PATCH (save).
    LANDING: (id: string) => `${baseUrl}/diplomas/${id}/landing`,
    LANDING_SUMMARIES: `${baseUrl}/diplomas/landing-summaries`,
    LEADS: (id: string, kind?: string) =>
      `${baseUrl}/diplomas/${id}/leads${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`,
  },
  // Tracked lead forms (lead-forms SPEC §C.3, §C.4; mounted at /v1/lead-forms).
  // Roles: admin, super-admin, sales-manager, marketing (LEAD_FORM_ROLES /
  // LEAD_INBOX_ROLES); DELETE is admin / super-admin only. The static paths
  // (diplomas, tracking-status, leads…) are declared before :id on the BE.
  LEAD_FORMS: {
    // Every diploma (active first) with its forms: the picker for roles that cannot call /diplomas.
    DIPLOMAS: `${baseUrl}/lead-forms/diplomas`,
    LIST: (q: LeadFormsQuery = {}) => withQuery(`${baseUrl}/lead-forms`, leadFormsQuery(q)),
    CREATE: `${baseUrl}/lead-forms`,
    TRACKING_STATUS: `${baseUrl}/lead-forms/tracking-status`,
    // GET (editor payload), PATCH {name?, key?, isPrimary?}, DELETE (no data only).
    BY_ID: (id: string) => `${baseUrl}/lead-forms/${id}`,
    DRAFT: (id: string) => `${baseUrl}/lead-forms/${id}/draft`,
    PUBLISH: (id: string) => `${baseUrl}/lead-forms/${id}/publish`,
    PAUSE: (id: string) => `${baseUrl}/lead-forms/${id}/pause`,
    RESUME: (id: string) => `${baseUrl}/lead-forms/${id}/resume`,
    ARCHIVE: (id: string) => `${baseUrl}/lead-forms/${id}/archive`,
    UNARCHIVE: (id: string) => `${baseUrl}/lead-forms/${id}/unarchive`,
    DUPLICATE: (id: string) => `${baseUrl}/lead-forms/${id}/duplicate`,
    PREVIEW_TOKEN: (id: string) => `${baseUrl}/lead-forms/${id}/preview-token`,
    REVISIONS: (id: string) => `${baseUrl}/lead-forms/${id}/revisions`,
    REVISION: (id: string, n: number) => `${baseUrl}/lead-forms/${id}/revisions/${n}`,
    // Phase 2: the funnel report and the A/B comparison of one form.
    FUNNEL: (id: string, q: LeadFormsQuery = {}) =>
      withQuery(`${baseUrl}/lead-forms/${id}/funnel`, leadFormsQuery(q)),
    AB: (id: string, q: LeadFormsQuery = {}) =>
      withQuery(`${baseUrl}/lead-forms/${id}/ab`, leadFormsQuery(q)),
    // Leads inbox. Paging keys are page / pageSize (any other key is a 400).
    LEADS: (q: LeadFormsQuery = {}) => withQuery(`${baseUrl}/lead-forms/leads`, leadFormsQuery(q)),
    LEADS_SUMMARY: `${baseUrl}/lead-forms/leads/summary`,
    LEADS_EXPORT_CSV: (q: LeadFormsQuery = {}) =>
      withQuery(`${baseUrl}/lead-forms/leads/export.csv`, leadFormsQuery(q)),
    LEAD: (id: string) => `${baseUrl}/lead-forms/leads/${id}`,
    LEAD_STATUS: (id: string) => `${baseUrl}/lead-forms/leads/${id}/status`,
    LEAD_NOTES: (id: string) => `${baseUrl}/lead-forms/leads/${id}/notes`,
    // SPEC-P P.2.7: POST (204) clears a lead's "updated, not seen yet" mark when staff open it.
    LEAD_SEEN: (id: string) => `${baseUrl}/lead-forms/leads/${id}/seen`,
    // Phase 3 (F.6, F.9): "Resend to Meta" with the same eventId ({event}).
    LEAD_META_RESEND: (id: string) => `${baseUrl}/lead-forms/leads/${id}/meta-resend`,
    // SPEC-R.5.1 Leads → Not sent (almost leads): the same query keys as the leads inbox.
    NOT_SENT: (q: LeadFormsQuery = {}) =>
      withQuery(`${baseUrl}/lead-forms/leads/not-sent`, leadFormsQuery(q)),
    NOT_SENT_SUMMARY: `${baseUrl}/lead-forms/leads/not-sent/summary`,
    NOT_SENT_EXPORT_CSV: (q: LeadFormsQuery = {}) =>
      withQuery(`${baseUrl}/lead-forms/leads/not-sent/export.csv`, leadFormsQuery(q)),
    NOT_SENT_ONE: (id: string) => `${baseUrl}/lead-forms/leads/not-sent/${id}`,
    NOT_SENT_STATUS: (id: string) => `${baseUrl}/lead-forms/leads/not-sent/${id}/status`,
    NOT_SENT_NOTES: (id: string) => `${baseUrl}/lead-forms/leads/not-sent/${id}/notes`,
    // SPEC-Q.4.1 short links: a form's list (GET) and create (POST), the custom-name check, and disable (admin only).
    SHORT_LINKS: (id: string) => `${baseUrl}/lead-forms/${id}/short-links`,
    SHORT_LINK_CHECK: (alias: string) => withQuery(`${baseUrl}/lead-forms/short-links/check`, { alias }),
    SHORT_LINK_DISABLE: (code: string) =>
      `${baseUrl}/lead-forms/short-links/${encodeURIComponent(code)}/disable`,
    // SPEC-S.2.2 the filtered CSV export (inbox roles): options (GET), count and file (POST, the same filter body).
    LEADS_EXPORT_OPTIONS: (q: LeadFormsQuery = {}) =>
      withQuery(`${baseUrl}/lead-forms/leads/export/options`, leadFormsQuery(q)),
    LEADS_EXPORT_COUNT: `${baseUrl}/lead-forms/leads/export/count`,
    LEADS_EXPORT: `${baseUrl}/lead-forms/leads/export`,
    // SPEC-S.9 the live Google Sheet: GET sheet (every inbox role), the rest admin / super-admin.
    SHEET: `${baseUrl}/lead-forms/sheet`,
    SHEET_SETTINGS: `${baseUrl}/lead-forms/sheet/settings`,
    SHEET_SECRET: `${baseUrl}/lead-forms/sheet/secret`,
    SHEET_SECRET_CANCEL: `${baseUrl}/lead-forms/sheet/secret/cancel`,
    SHEET_SCRIPT: `${baseUrl}/lead-forms/sheet/script`,
    SHEET_TEST: `${baseUrl}/lead-forms/sheet/test`,
    SHEET_FAILED: `${baseUrl}/lead-forms/sheet/failed`,
    SHEET_RETRY_FAILED: `${baseUrl}/lead-forms/sheet/retry-failed`,
    SHEET_BACKFILL: `${baseUrl}/lead-forms/sheet/backfill`,
    // SPEC-T.9 a form's own Google Sheet (personal Google account): the binding (GET / POST Connect sheet / DELETE
    // Unbind), Update sheet, the bindings list and the Kayfa sheets for "Use an existing sheet". Every lead-form role.
    SHEET_BINDING: (id: string) => `${baseUrl}/lead-forms/${id}/sheet-binding`,
    SHEET_BINDING_RESYNC: (id: string) => `${baseUrl}/lead-forms/${id}/sheet-binding/resync`,
    SHEET_BINDINGS: `${baseUrl}/lead-forms/sheet/bindings`,
    SHEET_GOOGLE_SHEETS: `${baseUrl}/lead-forms/sheet/google-sheets`,
    // GET sheet?form=<id>: + that form's binding block (the inbox chip, SPEC-T.8.3).
    SHEET_FOR_FORM: (formId: string) => withQuery(`${baseUrl}/lead-forms/sheet`, { form: formId }),
  },
  // SPEC-T.9 the caller's own Google account (mounted at /v1/google). GET connection works when the server has no
  // Google set-up ({configured: false}); the list and force-disconnect are admin / super-admin only.
  GOOGLE: {
    CONNECTION: `${baseUrl}/google/connection`,
    CONNECTIONS: `${baseUrl}/google/connections`,
    OAUTH_START: `${baseUrl}/google/oauth/start`,
    DISCONNECT: `${baseUrl}/google/connection/disconnect`,
    FORCE_DISCONNECT: (userId: string) => `${baseUrl}/google/connections/${userId}/disconnect`,
  },
  // Batch Management (kayfa-be/src/modules/v1/batch-sessions, mounted at
  // /v1/batch-sessions). Everything is keyed on (diplomaId, batchName) —
  // batchName is passed as a query param and may contain spaces, so always
  // encodeURIComponent it. Session-scoped endpoints use the session's _id.
  BATCH_SESSIONS: {
    // GET all diploma batches — the management landing list (staff-accessible).
    LIST_BATCHES: `${baseUrl}/batch-sessions/batches`,
    // GET + PUT the schedule/scoring config for one batch. GET takes the
    // (diplomaId, batchName) query; PUT sends them in the body (BatchConfigInput).
    CONFIG: (diplomaId: string, batchName: string) =>
      `${baseUrl}/batch-sessions/config?diplomaId=${encodeURIComponent(
        diplomaId,
      )}&batchName=${encodeURIComponent(batchName)}`,
    SAVE_CONFIG: `${baseUrl}/batch-sessions/config`,
    // POST — regenerate the whole schedule from the saved config.
    GENERATE: (diplomaId: string, batchName: string) =>
      `${baseUrl}/batch-sessions/generate?diplomaId=${encodeURIComponent(
        diplomaId,
      )}&batchName=${encodeURIComponent(batchName)}`,
    // GET list + POST add. List takes the query; add sends the body.
    SESSIONS: (diplomaId: string, batchName: string) =>
      `${baseUrl}/batch-sessions/sessions?diplomaId=${encodeURIComponent(
        diplomaId,
      )}&batchName=${encodeURIComponent(batchName)}`,
    ADD_SESSION: `${baseUrl}/batch-sessions/sessions`,
    // PATCH edit + DELETE one session by its _id.
    SESSION_BY_ID: (id: string) => `${baseUrl}/batch-sessions/sessions/${id}`,
    // GET the attendance sheet + PUT the marks for one session.
    SESSION_ATTENDANCE: (id: string) =>
      `${baseUrl}/batch-sessions/sessions/${id}/attendance`,
    // POST a CSV/XLSX email sheet — matched students -> present, the rest of the
    // batch -> absent, for this session. Returns the refreshed sheet + a summary.
    SESSION_ATTENDANCE_IMPORT: (id: string) =>
      `${baseUrl}/batch-sessions/sessions/${id}/attendance/import`,
    // GET per-student completion score for the batch.
    PROGRESS: (diplomaId: string, batchName: string) =>
      `${baseUrl}/batch-sessions/progress?diplomaId=${encodeURIComponent(
        diplomaId,
      )}&batchName=${encodeURIComponent(batchName)}`,
    // GET the full student × session attendance grid + completion for a batch.
    ATTENDANCE_MATRIX: (diplomaId: string, batchName: string) =>
      `${baseUrl}/batch-sessions/attendance-matrix?diplomaId=${encodeURIComponent(
        diplomaId,
      )}&batchName=${encodeURIComponent(batchName)}`,
    // GET the same grid as a downloadable CSV.
    ATTENDANCE_EXPORT_CSV: (diplomaId: string, batchName: string) =>
      `${baseUrl}/batch-sessions/attendance-export.csv?diplomaId=${encodeURIComponent(
        diplomaId,
      )}&batchName=${encodeURIComponent(batchName)}`,
  },
  // Course materials (kayfa-be/src/modules/v1/materials, mounted at /v1/materials).
  // Keyed on (diplomaId, batchName) — NEVER the batch subdoc _id, which is
  // regenerated on every diploma save. batchName may contain spaces: always
  // encodeURIComponent it. Responses ARE { data }-wrapped.
  MATERIALS: {
    ADMIN_LIST: (q: {
      diplomaId: string; batchName?: string; sessionId?: string;
      scope?: string; status?: string; page?: number; pageSize?: number;
    }) => `${baseUrl}/materials/admin?${new URLSearchParams(
      Object.entries(q).filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ).toString()}`,
    ORPHANS: (diplomaId: string, batchName?: string) =>
      `${baseUrl}/materials/admin/orphans?diplomaId=${encodeURIComponent(diplomaId)}` +
      (batchName ? `&batchName=${encodeURIComponent(batchName)}` : ''),
    BY_ID: (id: string) => `${baseUrl}/materials/admin/${id}`,
    ADD: `${baseUrl}/materials/admin`,
    UPDATE: (id: string) => `${baseUrl}/materials/admin/${id}`,
    PUBLISH: (id: string) => `${baseUrl}/materials/admin/${id}/publish`,
    REATTACH: (id: string) => `${baseUrl}/materials/admin/${id}/reattach`,
    REORDER: `${baseUrl}/materials/admin/reorder`,   // PUT
    DELETE: (id: string) => `${baseUrl}/materials/admin/${id}`,
  },
  // Session reminders (EPIC E7) — kayfa-be/src/modules/v1/reminders, mounted at
  // /v1/reminders. The admin surface is READ-ONLY over an append-only ledger,
  // plus one audited, once-ever resend per row. Responses ARE { data }-wrapped.
  //
  // Every filter is a QUERY PARAM and arrives at the BE as a STRING (there is no
  // global ValidationPipe transform), so page/pageSize go out as numbers here and
  // are parsed server-side. batchName contains spaces and is encoded by
  // URLSearchParams — never build these URLs by hand.
  REMINDERS: {
    DELIVERIES: (q: {
      page?: number;
      pageSize?: number;
      diplomaId?: string;
      batchName?: string;
      sessionId?: string;
      userId?: string;
      offset?: string | number;
      channel?: string;
      state?: string;
      reason?: string;
      // Shortcut that overrides `state` and `reason` server-side. Only ever sent
      // as true; false is dropped by queryString.
      problems?: boolean;
      from?: string; // inclusive, ISO, on createdAt
      to?: string; // exclusive, ISO, on createdAt
    }) => `${baseUrl}/reminders/admin/deliveries?${queryString(q)}`,
    // Fetched INDEPENDENTLY of the table so a summary failure cannot blank the
    // grid. Takes only the four scoping filters.
    SUMMARY: (q: { from?: string; to?: string; diplomaId?: string; batchName?: string }) =>
      `${baseUrl}/reminders/admin/summary?${queryString(q)}`,
    // The per-session diagnostic AND pre-send preview. Writes nothing.
    AUDIENCE: (sessionId: string) =>
      `${baseUrl}/reminders/admin/sessions/${encodeURIComponent(sessionId)}/audience`,
    // POST with an empty body. 409 is a normal outcome (see §9.6).
    RESEND: (id: string) =>
      `${baseUrl}/reminders/admin/deliveries/${encodeURIComponent(id)}/resend`,
    // PUT { paused: boolean, reason?: string } — the runtime "Pause reminder
    // sends" switch. ADMIN / SUPER_ADMIN. Audited; no deploy or restart.
    SENDS_PAUSE: `${baseUrl}/reminders/admin/sends-pause`,
  },
  // Zoom accounts & host pool (EPIC E1) — kayfa-be/src/modules/v1/zoom, mounted
  // at /v1/zoom. Super-admin owns the accounts; coordinators assign hosts to
  // their batches. Responses ARE { data }-wrapped.
  //
  // THE CLIENT SECRET IS WRITE-ONLY. It goes out on POST /accounts and PATCH
  // /accounts/:id and comes back on NOTHING — there is no endpoint here that
  // returns it, masks it, or reports its length.
  //
  // TEST and IMPORT_HOSTS return HTTP 200 EVEN WHEN THEY FAILED: read `ok` and
  // `reason` from the body rather than relying on a thrown error.
  ZOOM: {
    // Cheap, credential-free health read. Safe on a box with no ZOOM_ENC_KEY —
    // it is what tells the page to render the "not configured" banner instead
    // of a wall of failed requests.
    STATUS: `${baseUrl}/zoom/status`,
    // GET list + POST connect.
    ACCOUNTS: `${baseUrl}/zoom/accounts`,
    // PATCH edit + DELETE disconnect. DELETE is refused with a 409 while a
    // live booking still points at one of the account's hosts.
    ACCOUNT: (id: string) => `${baseUrl}/zoom/accounts/${encodeURIComponent(id)}`,
    TEST: (id: string) => `${baseUrl}/zoom/accounts/${encodeURIComponent(id)}/test`,
    IMPORT_HOSTS: (id: string) => `${baseUrl}/zoom/accounts/${encodeURIComponent(id)}/import-hosts`,
    HOSTS: (q: {
      accountId?: string;
      diplomaId?: string;
      batchName?: string;
      state?: string;
      includeUnusable?: boolean;
      q?: string;
    }) => `${baseUrl}/zoom/hosts?${queryString(q)}`,
    HOST: (id: string) => `${baseUrl}/zoom/hosts/${encodeURIComponent(id)}`,
    // PUT. Body { hostId, diplomaId, batchName? }. Omit batchName for a
    // shortlist entry. 409 is a NORMAL outcome — read `messages`, not `message`.
    ASSIGNMENTS: `${baseUrl}/zoom/assignments`,
    // DELETE. Exactly ONE of (batchName) or (hostId) alongside diplomaId.
    UNASSIGN: (q: { diplomaId: string; batchName?: string; hostId?: string }) =>
      `${baseUrl}/zoom/assignments?${queryString(q)}`,
    CAPACITY: (q: { from?: string; to?: string; tz?: string; diplomaId?: string }) =>
      `${baseUrl}/zoom/capacity?${queryString(q)}`,
    CLASHES: (q: { from?: string; to?: string; tz?: string; diplomaId?: string }) =>
      `${baseUrl}/zoom/clashes?${queryString(q)}`,
  },
  // Live classrooms (EPIC E2) — kayfa-be/src/modules/v1/live-classes, mounted at
  // /v1/live-classes. Publishing a class, cancelling it, and the Zoom room that
  // serves it. Responses ARE { data }-wrapped.
  //
  // batchName IS ALWAYS A QUERY PARAM, NEVER A PATH SEGMENT — it contains
  // spaces. queryString() percent-encodes it; never build these URLs by hand.
  //
  // THESE ENDPOINTS 409 WITH AN ARRAY. AllExceptionsFilter emits
  // { error, message, messages[] } for an exception constructed with an array,
  // and leaves `message` as Nest's derived "Conflict Exception". Read
  // `messages[]` through zoomMessages() and NEVER render `message` — that is
  // INTEGRATION TRAP #1 and it is why apiMessage() is banned on this family.
  // The three refusals that arrive this way are `host_clash` (a reschedule or a
  // generate that would double-book a licence), `reminders_already_sent` (an
  // unpublish learners have already been told about) and `live_rooms_present`
  // (a regenerate that would strand billed Zoom rooms).
  //
  // A WRITE THAT RETURNS 200 DID NOT NECESSARILY DO ANYTHING. …/meeting,
  // …/meeting/sync and …/cancel all answer 200 and report what happened in the
  // body — "no Zoom host is assigned to this batch" is a correct outcome, not an
  // error. Read `created` / `synced` / `explanation`, not the status code.
  //
  // 409 "Another operation is already running on this class." is a possible
  // answer from EVERY write below (the per-session Redis lock). Treat it as
  // "retry in a moment", never as an error state.
  //
  // NOTHING HERE RETURNS A ZOOM URL EXCEPT …/start-link, whose response is a
  // BEARER CREDENTIAL: whoever holds the start_url becomes host of the meeting.
  // It is fetched per click, never stored, never rendered and never logged
  // (§4). The learner's join_url is issued by POST /live-classes/my/... only —
  // an endpoint this app has no business calling and does not.
  LIVE_CLASSES: {
    // GET — { total, items: StaffSessionView[], counts, zoom: ZoomReadinessView }.
    // Readable by ADMIN, SUPER_ADMIN, CUSTOMER_CARE and COORDINATOR.
    ADMIN_SESSIONS: (diplomaId: string, batchName: string) =>
      `${baseUrl}/live-classes/admin/sessions?${queryString({ diplomaId, batchName })}`,
    // GET — the standing health band's data source (§12.4). Same read roles.
    ADMIN_HEALTH: (diplomaId: string, batchName: string) =>
      `${baseUrl}/live-classes/admin/health?${queryString({ diplomaId, batchName })}`,
    // PATCH { published: boolean }. Unpublishing 409s once a reminder has gone
    // out — `detail.kind === 'reminders_already_sent'`.
    ADMIN_PUBLISH: (id: string) =>
      `${baseUrl}/live-classes/admin/sessions/${encodeURIComponent(id)}/publish`,
    // POST { published: boolean }. Per-session refusals are REPORTED in
    // `refused[]`, not thrown — this is not all-or-nothing.
    ADMIN_PUBLISH_ALL: (diplomaId: string, batchName: string) =>
      `${baseUrl}/live-classes/admin/sessions/publish-all?${queryString({ diplomaId, batchName })}`,
    // POST { reason?: string } (≤ 500 chars). Soft cancel: the row, its
    // attendance, its materials and the Zoom meeting id all survive.
    ADMIN_CANCEL: (id: string) =>
      `${baseUrl}/live-classes/admin/sessions/${encodeURIComponent(id)}/cancel`,
    // POST — create the room now. DELETE — delete the room, KEEP the class.
    ADMIN_MEETING: (id: string) =>
      `${baseUrl}/live-classes/admin/sessions/${encodeURIComponent(id)}/meeting`,
    // POST — dry run: is this class's Zoom host free at its time? Never writes,
    // never creates a room. 200 { free, code?, detail? }. The Move-room flow
    // asks it BEFORE deleting the old room.
    ADMIN_MEETING_CHECK: (id: string) =>
      `${baseUrl}/live-classes/admin/sessions/${encodeURIComponent(id)}/meeting/check`,
    // POST — re-send the time to Zoom and verify it took. Zoom's meeting PATCH
    // answers 204 with no body and silently ignores a start_time it dislikes, so
    // the verify is the only way to know.
    ADMIN_MEETING_SYNC: (id: string) =>
      `${baseUrl}/live-classes/admin/sessions/${encodeURIComponent(id)}/meeting/sync`,
    // POST — ADMIN / SUPER_ADMIN only, never impersonated. Returns a start_url
    // that makes its holder HOST. Hand it to window.location and drop it.
    ADMIN_START_LINK: (id: string) =>
      `${baseUrl}/live-classes/admin/sessions/${encodeURIComponent(id)}/start-link`,
    // POST — ask Zoom what it actually has for this batch: adopt orphans, flag
    // drift, list meetings with no matching class. Creates nothing, deletes
    // nothing.
    ADMIN_RECONCILE: (diplomaId: string, batchName: string) =>
      `${baseUrl}/live-classes/admin/reconcile?${queryString({ diplomaId, batchName })}`,
    // PUT { paused: boolean, reason?: string } (≤ 300). Pause or resume every
    // live class of the cohort. Rooms are KEPT. ADMIN, SUPER_ADMIN, COORDINATOR.
    ADMIN_PAUSE: (diplomaId: string, batchName: string) =>
      `${baseUrl}/live-classes/admin/batches/pause?${queryString({ diplomaId, batchName })}`,
    // POST — end the class now: learners are refused, the host link is
    // withheld, Zoom's participants are imported and the rest of the cohort is
    // marked absent. 409 before start−30 min (`too_early`), on a cancelled
    // class, with no room (`no_room`) or when already ended (`already_ended`).
    ADMIN_END: (id: string) =>
      `${baseUrl}/live-classes/admin/sessions/${encodeURIComponent(id)}/end`,
    // POST — reopen an ended class. 409 `reopen_window_closed` after the
    // scheduled end + 30 min. ADMIN, SUPER_ADMIN, COORDINATOR.
    ADMIN_REOPEN: (id: string) =>
      `${baseUrl}/live-classes/admin/sessions/${encodeURIComponent(id)}/reopen`,
  },
  // Session recordings (EPIC E6) — kayfa-be/src/modules/v1/recordings, mounted
  // at /v1/recordings by a RouterModule entry. Responses ARE { data }-wrapped.
  //
  // batchName IS ALWAYS A QUERY PARAM, NEVER A PATH SEGMENT — it contains
  // spaces. queryString() percent-encodes it; never build these URLs by hand.
  //
  // ROUTE ORDER IS LOAD-BEARING ON THE BE. The static admin routes are declared
  // before the parameterised ones, so GET /admin/health resolves as itself and
  // not as GET /admin/:id with id='health'. Nothing on this side can break that,
  // but it is why /admin/health, /admin/orphans, /admin/reconcile and
  // /admin/batches/... are NOT reachable through BY_ID below.
  //
  // NO ENDPOINT HERE RETURNS A STORAGE KEY OR AN OBJECT URL, EXCEPT PLAY, whose
  // response is a BEARER CREDENTIAL FOR THE VIDEO: whoever holds the presigned
  // URL can watch the class. It is minted per click, handed straight to a
  // <video> element and dropped — never stored, never placed in an href, never
  // copied to the clipboard, never put in a snackbar and never logged. That is
  // the same discipline E2's start-link follows, for the same reason.
  //
  // THE ONE DESTRUCTIVE CALL IS PURGE, and it destroys the STORED FILE, not the
  // row. It is super-admin only, it is refused outright unless the API was
  // started with RECORDING_PURGE_ENABLED=true, and its body must carry the row's
  // exact ingestKey typed back. Nothing else in this block removes anything:
  // archive hides, detach unbinds, skip declines to copy.
  RECORDINGS: {
    // GET — the seven bands + the platform facts (§12.5). Both filters are
    // optional: with neither, it is the whole platform (the Settings page);
    // with both, it is one cohort (the batch tab).
    ADMIN_HEALTH: (q: { diplomaId?: string; batchName?: string }) =>
      withQuery(`${baseUrl}/recordings/admin/health`, q),
    // GET — recordings Kayfa holds but cannot attach to a class.
    ADMIN_ORPHANS: (q: { page?: number; limit?: number }) =>
      withQuery(`${baseUrl}/recordings/admin/orphans`, q),
    // POST { diplomaId?, batchName? } — ask Zoom what it has now instead of
    // waiting for the fifteen-minute poll. Creates rows, deletes nothing.
    // 503 "Zoom is not connected." is a normal answer on a box with no account.
    ADMIN_RECONCILE: `${baseUrl}/recordings/admin/reconcile`,
    // GET + PUT the cohort's capture settings. batchName is REQUIRED on both
    // and is a query param on both — the 400 is "A batchName is required."
    ADMIN_BATCH_SETTINGS: (diplomaId: string, batchName: string) =>
      `${baseUrl}/recordings/admin/batches/${encodeURIComponent(diplomaId)}/settings?${queryString(
        { batchName },
      )}`,
    // GET — the admin table. Sending batchName without diplomaId is a 400.
    ADMIN_LIST: (q: {
      diplomaId?: string;
      batchName?: string;
      sessionId?: string;
      state?: string;
      status?: string;
      clearState?: string;
      q?: string;
      page?: number;
      limit?: number;
    }) => withQuery(`${baseUrl}/recordings/admin`, q),
    // GET — the detail view. The ONLY response that carries ingestKey, the Zoom
    // ids, the classified errors and the operator explanation. Still no key and
    // still no URL; `storageBucket` is the bucket NAME, for the operator banner.
    BY_ID: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}`,
    // PATCH { title?, title_ar?, order?, availableFrom? }.
    UPDATE: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}`,
    // POST — make it visible to the cohort. 409s on anything unverified,
    // unattached, archived, or whose stored file answered a public request.
    PUBLISH: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/publish`,
    UNPUBLISH: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/unpublish`,
    // POST — reset the attempt counter and re-queue. The only way out of the
    // attempt cap, and the only way back from a skip.
    RETRY: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/retry`,
    // POST { reason } (≥ 4 chars). Refused while the row is copying.
    SKIP: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/skip`,
    // POST — super-admin. A held row is never archived by retention and never
    // cleared from Zoom.
    HOLD: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/hold`,
    UNHOLD: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/unhold`,
    // POST — unbind from the class. The file survives; sessionOrder is kept so
    // a regenerated schedule can find it again.
    DETACH: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/detach`,
    // POST { sessionId }. ALWAYS returns to draft — re-attaching is not a
    // decision to show it to learners, and making that automatic is how a
    // recording of the wrong class goes live.
    REATTACH: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/reattach`,
    // POST { reason? } — hide it from learners. THE FILE IS KEPT.
    ARCHIVE: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/archive`,
    // POST — mint a short-lived playback URL for staff review. See the block
    // comment above: the answer is a credential, not a link.
    PLAY: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/play`,
    // DELETE { confirm } — super-admin, and only with RECORDING_PURGE_ENABLED.
    // DESTROYS THE STORED FILE. The row survives as the record that it existed.
    PURGE: (id: string) => `${baseUrl}/recordings/admin/${encodeURIComponent(id)}/purge`,
  },
  // PARTNER PLATFORM S1 — applications, partners, agreements.
  //
  // READ OFF THE CONTROLLERS, NOT OFF THE EPIC DOCUMENT. The API is FOUR
  // modules mounted by v1.module.ts's RouterModule:
  //
  //   /v1/partner-applications        PartnerApplicationsModule
  //   /v1/admin/partners              PartnersModule
  //   /v1/admin/partner-agreements    PartnerAgreementsModule
  //   /v1/partner                     PartnerPortalModule — the partner's own
  //                                   surface. NOT ours; nothing here touches it.
  //
  // THE QUEUE IS NOT UNDER /admin, AND A MISTYPED PATH HERE DOES NOT FAIL
  // LOUDLY. `GET /v1/admin/partners/applications` matches `GET /admin/partners/:id`
  // with id 'applications' and answers a plain 404 with the service's own
  // sentence — indistinguishable from a partner who does not exist. That is why
  // every entry below names its VERB and its GUARD as well as its path.
  //
  // THE RULES THAT APPLY AT EVERY CALL SITE:
  //   * Responses are { data }-wrapped — `res.data?.data ?? res.data`, always.
  //   * `page` is 1-INDEXED ON THE WIRE, 0-indexed in MUI. The page-size param
  //     is `limit`, NOT `pageSize`: an unrecognised query key is silently
  //     ignored and the server quietly pages at its own default instead.
  //   * A 409 is a normal outcome on claim/release/approve/reject/publish and
  //     is rendered INLINE in the open dialog, never as a toast.
  //   * THERE IS NO PATCH AND NO DELETE ON AN AGREEMENT VERSION. Not behind a
  //     role, not disabled — the route does not exist. Editing happens on the
  //     DRAFT, which is a row in a different collection.
  //   * No response carries a credential.
  PARTNERS: {
    // ── /v1/partner-applications ────────────────────────────────────────────
    //
    // GET — the review queue. @Auth(ADMIN, SUPER_ADMIN). NOT customer-care:
    // there is no non-admin read of an application anywhere on this platform.
    // Server-projected — the row carries the applicant's name, email,
    // organization, country and expertise and NOT their phone, message,
    // proposed courses, links, ipHash or userAgent.
    //
    // `status` takes one of the four wire values OR the shorthand 'open', which
    // the repository expands to submitted+in_review — the same pair that is
    // index #1's partialFilterExpression. `claimedBy` is a USER ID and is
    // ignored unless it is a valid ObjectId. The response carries `counts` and
    // `intakeEnabled`, so the header tiles and the amber kill-switch banner
    // need no second request that could fail on its own.
    APPLICATIONS: (q: {
      status?: string;
      claimedBy?: string;
      q?: string;
      page?: number;
      limit?: number;
    }) => withQuery(`${baseUrl}/partner-applications`, q),
    // GET — the PII surface: phone, message, proposed courses, links, internal
    // notes, ipHash, userAgent, plus `preflight`, recomputed on EVERY read so
    // the reviewer banner cannot go stale between opening the page and pressing
    // the button. @Auth(ADMIN, SUPER_ADMIN).
    APPLICATION: (id: string) =>
      `${baseUrl}/partner-applications/${encodeURIComponent(id)}`,
    // POST, NO BODY — take/return the review. A CAS, so a second reviewer gets
    // a 409 rather than silently stealing the row. A SUPER_ADMIN may break
    // somebody else's claim; anybody else may only release their own.
    APPLICATION_CLAIM: (id: string) =>
      `${baseUrl}/partner-applications/${encodeURIComponent(id)}/claim`,
    APPLICATION_RELEASE: (id: string) =>
      `${baseUrl}/partner-applications/${encodeURIComponent(id)}/release`,
    // PATCH { internalNotes } — STAFF-ONLY working notes, and a REPLACEMENT of
    // the whole field rather than an append. Send the edited full text or every
    // earlier note is destroyed. Never emailed, never shown to the applicant.
    APPLICATION_NOTES: (id: string) =>
      `${baseUrl}/partner-applications/${encodeURIComponent(id)}/notes`,
    // POST, **NO BODY**. The partner's name, slug and locale are derived
    // SERVER-SIDE from the application — nothing the reviewer types is sent.
    // Mints or LINKS a login and creates the Partner. @NoImpersonation.
    // The response carries `accountCreated` and `inviteSent` and NO password,
    // NO token and NO invite URL.
    APPLICATION_APPROVE: (id: string) =>
      `${baseUrl}/partner-applications/${encodeURIComponent(id)}/approve`,
    // POST { reason } — 20..2000 characters, MANDATORY, emailed to the
    // applicant VERBATIM in their own language together with the date they may
    // apply again. `reason` is the ONLY key; there is no `confirm`.
    APPLICATION_REJECT: (id: string) =>
      `${baseUrl}/partner-applications/${encodeURIComponent(id)}/reject`,

    // ── /v1/admin/partners ──────────────────────────────────────────────────
    //
    // GET (ADMIN, SUPER_ADMIN) + PATCH (SUPER_ADMIN, @NoImpersonation).
    // `intakeEnabled` is the kill switch; `ledgerChainOk` / `ledgerCheckedAt` /
    // `ledgerFailedPartners` are the NIGHTLY cron's verdict and are the only
    // PLATFORM-WIDE integrity fact any endpoint on this API carries.
    // Declared before BY_ID for the same reason the controller declares
    // `GET /settings` before `GET /:id`.
    SETTINGS: `${baseUrl}/admin/partners/settings`,
    // GET — the partner directory. @Auth(ADMIN, SUPER_ADMIN).
    LIST: (q: { status?: string; q?: string; page?: number; limit?: number }) =>
      withQuery(`${baseUrl}/admin/partners`, q),
    // GET one partner. @Auth(ADMIN, SUPER_ADMIN).
    //
    // THERE IS NO PATCH ON THIS PATH. A partner's name, slug and contact email
    // are derived at approval from the application and are not editable from
    // this app at all; the only mutation on a partner is the status transition
    // below. A profile edit form here would be a form whose Save button 404s.
    BY_ID: (id: string) => `${baseUrl}/admin/partners/${encodeURIComponent(id)}`,
    // PATCH { status: 'active'|'suspended'|'terminated', reason } — a written
    // reason of 20..1000 characters is MANDATORY. 'pending' is NOT settable: it
    // is the state a partner is born in, not one they can be put back into.
    // @NoImpersonation.
    STATUS: (id: string) =>
      `${baseUrl}/admin/partners/${encodeURIComponent(id)}/status`,

    // ── /v1/admin/partner-agreements ────────────────────────────────────────
    //
    // A DIFFERENT PREFIX FROM THE PARTNER, ON PURPOSE: this controller takes a
    // :partnerId FROM THE PATH — the exact shape the partner-facing portal is
    // forbidden to use — so "an admin may read any partner" and "a partner may
    // read one partner" can never end up in the same file.
    //
    // GET + PUT — the working draft, in a DIFFERENT COLLECTION from a published
    // version. That separation is what makes `immutable` on 23 sealed paths
    // workable: nothing ever needs to edit a version, so nothing needs an
    // escape hatch from the immutability. Every field on the PUT is optional
    // and only the paths actually sent are written, so saving one field cannot
    // blank the fourteen a colleague set an hour ago. `missing` and `complete`
    // are computed server-side, so the completeness rule exists in one place.
    AGREEMENT_DRAFT: (partnerId: string) =>
      `${baseUrl}/admin/partner-agreements/${encodeURIComponent(partnerId)}/draft`,
    // POST, **NO BODY**. THE IRREVERSIBLE ONE. It seals whatever is in the
    // DRAFT — so the confirmation dialog must display the draft the SERVER
    // returned, re-read immediately beforehand, and never a form's local state.
    // A dialog that names one revenue share and commits another is the failure
    // this endpoint's shape makes possible.
    AGREEMENT_PUBLISH: (partnerId: string) =>
      `${baseUrl}/admin/partner-agreements/${encodeURIComponent(partnerId)}/publish`,
    // GET — every version, OLDEST FIRST. A BARE ARRAY, not an envelope.
    AGREEMENT_VERSIONS: (partnerId: string) =>
      `${baseUrl}/admin/partner-agreements/${encodeURIComponent(partnerId)}/versions`,
    // GET ?at=<ISO-8601> — "which terms applied on the 3rd of March?", the
    // question a payout dispute actually asks. Omitting `at` means now; an
    // unparseable `at` is a 400 and never a silent "no terms applied".
    AGREEMENT_IN_FORCE: (partnerId: string, at?: string) =>
      withQuery(
        `${baseUrl}/admin/partner-agreements/${encodeURIComponent(
          partnerId,
        )}/versions/in-force`,
        { at },
      ),
    // GET one version. There is deliberately no PATCH beside it.
    AGREEMENT_VERSION: (partnerId: string, versionId: string) =>
      `${baseUrl}/admin/partner-agreements/${encodeURIComponent(
        partnerId,
      )}/versions/${encodeURIComponent(versionId)}`,
    // GET — recompute this partner's whole HMAC chain NOW: `chainOk`, a
    // per-version verdict naming the expected and actual hash PREFIXES, and
    // `firstBadVersion`. A GET because it reads and verifies and NEVER repairs:
    // a chain that the system which failed to protect it can also fix proves
    // nothing at all.
    LEDGER: (partnerId: string) =>
      `${baseUrl}/admin/partner-agreements/${encodeURIComponent(partnerId)}/ledger`,
  },
  // PARTNER PLATFORM S2 — the creator studio's ADMIN REVIEW surface.
  //
  // READ OFF §12.B OF PARTNER_S2_SPEC.md, WHICH IS ITSELF READ OFF THE
  // CONTROLLER. ONE module, mounted by v1.module.ts's RouterModule:
  //
  //   /v1/admin/partner-courses   PartnerCourseReviewModule
  //
  // The studio's own surface — /v1/partner/studio/* — is the PARTNER's, is
  // @Auth(RolesEnum.PARTNER) + @NoImpersonation, and NOTHING HERE TOUCHES IT.
  // A partner holds no admin token and this app holds no partner one; the two
  // surfaces are separate modules on the backend precisely so "an admin may read
  // any partner's course" and "a partner may read one partner's course" can
  // never end up in the same file.
  //
  // ── ROUTE DECLARATION ORDER IS LOAD-BEARING, ON BOTH SIDES ──────────────
  //
  // The controller declares `GET /revisions`, `GET /revisions/:revisionId` and
  // `GET|PATCH /settings/course-defaults` BEFORE `GET /:courseId`. Get that
  // wrong on either side and `/v1/admin/partner-courses/revisions` resolves as a
  // course whose id is the literal string "revisions" — answering the service's
  // own 404 rather than the router's, which is indistinguishable from a course
  // that does not exist. Every entry below names its VERB and its GUARD as well
  // as its path, for exactly the reason the S1 block above gives.
  //
  // ── THE RULES THAT APPLY AT EVERY CALL SITE ────────────────────────────
  //
  //   * Responses are { data }-wrapped — `res.data?.data ?? res.data`, always.
  //     `data` IS A BANNED FIELD NAME inside a response body: the interceptor
  //     hoists a top-level `data` key and DISCARDS EVERY SIBLING, so no view in
  //     this epic emits one.
  //   * `page` is 1-INDEXED ON THE WIRE, 0-indexed in MUI. The page-size param
  //     is `pageSize` — NOT `limit`. Stage 2 genuinely differs from Stage 1
  //     here, and an unrecognised query key is SILENTLY IGNORED (the global
  //     ValidationPipe runs with no whitelist), so the wrong spelling pages at
  //     the server's own default while the footer claims otherwise.
  //   * EVERY WRITE RETURNS 200 WITH A BODY. There is no 204 anywhere in this
  //     epic: a void handler is mapped to `null` by the interceptor, which
  //     fights a 204's must-be-empty body for no benefit.
  //   * ERROR BODIES CARRY NO `statusCode`. Branch on `err.error` (the
  //     exception class name) and read `err.message`; `detail` is additive and
  //     is forwarded only on 409/413/422/429/503. `httpStatusOf()` in
  //     recordingLabels derives the number from `err.error` and is the only
  //     thing on this side that should ever produce one.
  //   * A 409 is a NORMAL outcome on approve/reject/unpublish/republish — it
  //     means another reviewer decided first — and is rendered INLINE in the
  //     open dialog, never as a toast.
  //   * NOTHING IS EVER HARD-DELETED. There is no DELETE verb on this surface at
  //     all: unpublish sets `active: false` and keeps every enrolment row.
  //   * The playback response IS A CREDENTIAL, not a link.
  PARTNER_COURSES: {
    // GET — the review queue. @Auth(ADMIN, SUPER_ADMIN). Sorted
    // `partnerSubmittedAt: 1` ASCENDING — oldest first, because it is a queue
    // and not a stack. `q` is regex-ESCAPED server-side (an admin search box is
    // not a place to accept a regex). `state` must be one of
    // PARTNER_COURSE_STATES or the request is 400 — there is no `open`
    // shorthand here, unlike the S1 applications queue.
    LIST: (q: {
      state?: string;
      partnerId?: string;
      partnerStatus?: string;
      q?: string;
      page?: number;
      pageSize?: number;
    }) => withQuery(`${baseUrl}/admin/partner-courses`, q),
    // GET — the FULL course body, for the quality check. @Auth(ADMIN,
    // SUPER_ADMIN).
    //
    // DELIBERATELY NOT `GET /v1/courses/:id`: that path runs stripLockedMedia,
    // which strips exactly the media the reviewer exists to check — and, until
    // the P0-4 fix lands on the backend, strips it even for an admin, because
    // the staff bypass reads `user.role` (singular) while every live user row
    // carries `roles`. Carries the server-computed `checklist[]` and the five
    // `can*` affordances.
    BY_ID: (courseId: string) =>
      `${baseUrl}/admin/partner-courses/${encodeURIComponent(courseId)}`,
    // GET — the Partner courses tab categories PUBLISHED partner courses use,
    // `{ items: [{ name, count }] }`. @Auth(ADMIN, SUPER_ADMIN). Suggestions for
    // the approve dialog, so "iot" is not approved beside an existing "IoT".
    CATEGORIES: `${baseUrl}/admin/partner-courses/categories`,
    // POST { note?, slug?, order?, instructorId?, partnerCategory? }.
    // `partnerCategory` replaces the partner's; absent keeps it — and the result
    // must be non-empty, or the API answers 400.
    // @Auth(ADMIN, SUPER_ADMIN). THE ONLY PATH IN THE CODEBASE THAT WRITES
    // `partnerState: 'published'` TOGETHER WITH `active: true`, and it does so
    // in ONE atomic $set behind a compare-and-swap naming the from-state, so two
    // reviewers deciding at once produce one winner and one 409.
    //
    // `note` is NOT the row's reason — an approval stores `reason: ''`. The note
    // is the reviewer's record of what they checked.
    APPROVE: (courseId: string) =>
      `${baseUrl}/admin/partner-courses/${encodeURIComponent(courseId)}/approve`,
    // POST { reason } — 20..1000 characters, MANDATORY. @Auth(ADMIN,
    // SUPER_ADMIN). Returns the course to `draft` so the partner can fix it and
    // resubmit; `rejected` is deliberately NOT a course state, because a
    // terminal one strands their work where they can neither edit nor resubmit.
    // `active` is not written — it is already false.
    REJECT: (courseId: string) =>
      `${baseUrl}/admin/partner-courses/${encodeURIComponent(courseId)}/reject`,
    // POST { reason } — pull a LIVE course. @Auth(ADMIN, SUPER_ADMIN).
    // NOTHING IS DELETED: enrolled students keep the course, their watchedItems,
    // their watchEvents and their quizAttempts. The response reports
    // `enrolledStudentsRetained`, counted at the moment of the write, so the
    // reviewer sees that on the screen. Reversible via REPUBLISH.
    UNPUBLISH: (courseId: string) =>
      `${baseUrl}/admin/partner-courses/${encodeURIComponent(courseId)}/unpublish`,
    // POST { reason } — put it back. @Auth(ADMIN, SUPER_ADMIN). Refused with an
    // AUDITED 409 when the partner is not active, exactly like approve.
    REPUBLISH: (courseId: string) =>
      `${baseUrl}/admin/partner-courses/${encodeURIComponent(courseId)}/republish`,
    // POST { reason } — SUPER_ADMIN ONLY, + @NoImpersonation.
    //
    // $unset of partner/partnerState/partnerSubmittedAt/partnerPublishedAt,
    // leaving an ordinary Kayfa course editable in the existing admin editor.
    // `active` is LEFT EXACTLY AS IT WAS. `partnerReview` is KEPT, with
    // `outcome: 'transferred'`, because it is the only record on the row of
    // where the course came from. This is the supported end state for a
    // terminated partner's live course.
    TRANSFER: (courseId: string) =>
      `${baseUrl}/admin/partner-courses/${encodeURIComponent(courseId)}/transfer-to-kayfa`,
    // GET — the revision queue. @Auth(ADMIN, SUPER_ADMIN). DECLARED BEFORE
    // BY_ID above for the same reason the controller declares it first.
    REVISIONS: (q: { state?: string; partnerId?: string; page?: number; pageSize?: number }) =>
      withQuery(`${baseUrl}/admin/partner-courses/revisions`, q),
    // GET one revision, with `live.sections` and `proposed.sections` so the
    // before/after diff is rendered from two server-sent trees and never from a
    // merge computed in the browser. @Auth(ADMIN, SUPER_ADMIN).
    REVISION: (revisionId: string) =>
      `${baseUrl}/admin/partner-courses/revisions/${encodeURIComponent(revisionId)}`,
    // POST { note? } — CAS the revision to `applied`, then ONE $set copying its
    // `sections` onto the live course. @Auth(ADMIN, SUPER_ADMIN).
    // THE LIVE COURSE'S `partnerState` IS NOT WRITTEN AND `active` IS NOT
    // WRITTEN — a course with students in it never blinks out of the catalogue
    // because its author started editing. 409 when the live course moved since
    // the revision was opened (a Tier B edit landed): the partner must reopen.
    REVISION_APPROVE: (revisionId: string) =>
      `${baseUrl}/admin/partner-courses/revisions/${encodeURIComponent(revisionId)}/approve`,
    // POST { reason } — 20..1000 characters, MANDATORY. @Auth(ADMIN,
    // SUPER_ADMIN). The revision row is MARKED, not deleted.
    REVISION_REJECT: (revisionId: string) =>
      `${baseUrl}/admin/partner-courses/revisions/${encodeURIComponent(revisionId)}/reject`,
    // GET — mint a short-lived playback URL for the reviewer. @Auth(ADMIN,
    // SUPER_ADMIN). `Cache-Control: no-store`.
    //
    // THE ANSWER IS A CREDENTIAL, NOT A LINK — the same posture as
    // RECORDINGS.PLAY above. It is minted on the REVIEWER's identity, an audit
    // row carrying `mediaId` and `courseId` (and NEVER the URL, NEVER the
    // storageKey) is written, and the URL is handed straight to a <video>
    // element or an <a target=_blank> and dropped.
    MEDIA_PLAYBACK: (courseId: string, mediaId: string) =>
      `${baseUrl}/admin/partner-courses/${encodeURIComponent(
        courseId,
      )}/media/${encodeURIComponent(mediaId)}/playback`,
    // GET (ADMIN, SUPER_ADMIN) + PATCH (SUPER_ADMIN, @NoImpersonation).
    //
    // The platform-owned parts of a partner course: the track, category,
    // package, instructor byline and certificate template every studio-created
    // row is stamped with, plus the price caps. UNTIL A SUPER_ADMIN SETS THEM,
    // the studio refuses course creation with 503 "The studio is not
    // configured." — the same ships-closed posture as S1's `intakeEnabled`.
    // `ready` is the SERVER's verdict; this app never recomputes completeness.
    // PATCH verifies each id resolves to a live row before writing, because a
    // dangling FK would make every studio creation fail at Mongoose validation
    // with a message no partner can act on.
    COURSE_DEFAULTS: `${baseUrl}/admin/partner-courses/settings/course-defaults`,
  },
  // BUNNY STREAM — the admin operations surface (BUNNY_SPEC.md §9.D).
  //
  // ONE module, mounted by v1.module.ts's RouterModule at a prefix DECLARED
  // BEFORE `/admin/partner-courses`:
  //
  //   /v1/admin/bunny   BunnyModule (its admin controller)
  //
  // The partner's own Bunny routes — /v1/partner/studio/media/video/* — are
  // @Auth(RolesEnum.PARTNER) + @NoImpersonation() and NOTHING HERE TOUCHES THEM,
  // exactly as nothing in PARTNER_COURSES touches /v1/partner/studio. The
  // webhook — POST /v1/bunny/webhook — has no @Auth at all and is authenticated
  // by an HMAC over the raw request body; it is a provider-to-server path and no
  // browser ever calls it.
  //
  // ── THE GUARDS ─────────────────────────────────────────────────────────
  //
  //   @Auth(ADMIN, SUPER_ADMIN)  on the class — D1..D8
  //   @Auth(SUPER_ADMIN)         on D9 (purge) ONLY
  //
  // D9 is therefore ABSENT rather than disabled for an ADMIN, the same posture
  // as TRANSFER above and for the same reason (hooks/usePartnerOps.ts).
  //
  // ── THE RULES THAT APPLY AT EVERY CALL SITE ────────────────────────────
  //
  //   * Responses are { data }-wrapped — `res.data?.data ?? res.data`, always.
  //   * `page` is 1-INDEXED ON THE WIRE, 0-indexed in MUI. The page-size param
  //     is `pageSize`, NOT `limit` — this surface pages like Stage 2, and
  //     `pageSize` is clamped to 100 server-side by parsePaging().
  //   * EVERY QUERY PARAMETER ARRIVES AS A STRING. There is no global
  //     ValidationPipe transform on this API, so `stuck` is the literal string
  //     'true' (the house spelling: partner.constants.ts:313,
  //     partner-course.constants.ts:495, recording.constants.ts:365) and
  //     withQuery() drops `false` so an unset filter never reaches the BE.
  //   * ERROR BODIES CARRY NO `statusCode`. Branch on `err.error` (the exception
  //     class name) and render `err.message` through partnerMessages().
  //   * THE ADMIN SURFACE NAMES ITS REFUSALS. Every 409 and 503 below is a full
  //     sentence written for an operator and is rendered VERBATIM, inline in the
  //     dialog that caused it — never as a toast and never paraphrased.
  //   * NOTHING IS EVER HARD-DELETED EXCEPT THROUGH D9. Archive is a KAYFA-SIDE
  //     state transition that deliberately does not call the provider's delete;
  //     the bytes stay. D9 exists only to clean up zero-byte orphans and is
  //     refused unless the row is `failed`, holds no bytes, `confirm` is true and
  //     the server's own purge flag is on.
  //   * NO RESPONSE CARRIES A CREDENTIAL. No key, no token, no signed URL. The
  //     one identifier that IS emitted here and nowhere else is `videoGuid`,
  //     because an operator needs it to open the provider's dashboard.
  BUNNY: {
    // D1 · GET — the whole operator picture in one call. @Auth(ADMIN,
    // SUPER_ADMIN).
    //
    // IT ANSWERS 200 WITH NO KEY. That is the entire point of it: credentials do
    // not exist yet, degraded is the SHIPPING state, and a screen that spun
    // forever or showed a wall of red would hide the one sentence naming the
    // variable somebody has to set.
    STATUS: `${baseUrl}/admin/bunny/status`,
    // D2 · GET — the row list. @Auth(ADMIN, SUPER_ADMIN).
    //
    // `state` is one of the six BUNNY_VIDEO_STATES; `stuck` is 'true' or absent;
    // `courseId` and `partnerId` are mongo ids. `pageSize` is clamped to 100.
    VIDEOS: (q: {
      state?: string;
      courseId?: string;
      partnerId?: string;
      stuck?: string;
      page?: number;
      pageSize?: number;
    }) => withQuery(`${baseUrl}/admin/bunny/videos`, q),
    // D3 · GET one row PLUS ONE LIVE FETCH of the provider's video object.
    // @Auth(ADMIN, SUPER_ADMIN). When the provider cannot be asked the response
    // is still 200, with `bunny: null` and a `bunnyError` sentence — never a 500
    // and never an exception. 404 `Video not found.`
    VIDEO: (id: string) => `${baseUrl}/admin/bunny/videos/${encodeURIComponent(id)}`,
    // D4 · POST, NO BODY — force one poll now, and return the D3 body.
    // @Auth(ADMIN, SUPER_ADMIN). Rate limited to one per row per ten seconds:
    // 429 `Refresh is rate limited. Try again in a few seconds.` Never a 500 —
    // a provider failure comes back as the row with `bunnyError`.
    VIDEO_REFRESH: (id: string) =>
      `${baseUrl}/admin/bunny/videos/${encodeURIComponent(id)}/refresh`,
    // D5 · POST, NO BODY — THE RETRY. @Auth(ADMIN, SUPER_ADMIN).
    //
    // 409 `Only a failed or ready video can be re-encoded.`
    // 409 `This video cannot be re-encoded because the library did not keep
    //      original files.` — THE ONE SETTINGS MISTAKE THAT IS PERMANENT. Keep
    //      Original Files is a pre-upload library setting and is not
    //      retrofittable: turning it on today does nothing for anything uploaded
    //      before. There is no way to fix that from this app or any other.
    // 503 `Bunny Stream is not configured.`
    VIDEO_REENCODE: (id: string) =>
      `${baseUrl}/admin/bunny/videos/${encodeURIComponent(id)}/reencode`,
    // D6 · POST { reason } — 20..1000 characters, MANDATORY, the same rule as
    // every other decision reason on the partner surface. @Auth(ADMIN,
    // SUPER_ADMIN).
    //
    // NO CALL IS MADE TO THE PROVIDER AND THE BYTES STAY. Archive is a
    // Kayfa-side state transition; the provider has no archive, and DELETE is
    // its only removal primitive. Playback tokens stop being minted, so the
    // lesson stops playing.
    //
    // AN ARCHIVE DOES NOT UNPUBLISH THE COURSE. The course stays exactly as live
    // as it was; unpublishing is a different button on a different screen.
    // 409 `Only a ready video can be archived.`
    VIDEO_ARCHIVE: (id: string) =>
      `${baseUrl}/admin/bunny/videos/${encodeURIComponent(id)}/archive`,
    // D7 · POST, NO BODY — re-polls the provider FIRST and restores to `ready`
    // only if the video object is still finished; otherwise the row lands on
    // `failed` / video missing. @Auth(ADMIN, SUPER_ADMIN).
    // 409 `Only an archived video can be restored.`
    VIDEO_UNARCHIVE: (id: string) =>
      `${baseUrl}/admin/bunny/videos/${encodeURIComponent(id)}/unarchive`,
    // D8 · POST, NO BODY — UNLINK, NOT DELETE. @Auth(ADMIN, SUPER_ADMIN).
    //
    // Sets `detachedAt` and clears `itemUuid` ON OUR ROW ONLY. IT DOES NOT WRITE
    // TO `course.sections`: an admin writing into a partner's sections array is
    // a boundary crossing with no optimistic-concurrency token and it would race
    // the partner's own editor. The lesson then reads exactly as if it had no
    // media, which is what the partner has to do something about, and the video
    // stays at the provider and stays re-attachable.
    VIDEO_DETACH: (id: string) =>
      `${baseUrl}/admin/bunny/videos/${encodeURIComponent(id)}/detach`,
    // D9 · POST { confirm, reason } — @Auth(SUPER_ADMIN). THE ONLY PATH IN THE
    // ENTIRE EPIC THAT MAY REACH THE PROVIDER'S DELETE.
    //
    // Refused unless ALL FOUR hold:
    //   503 `Purge is disabled. Set BUNNY_PURGE_ENABLED=true to enable it.`
    //   409 `Only a failed video can be purged. Archive retains learner content;
    //        it is never destroyed.`
    //   409 `This video holds bytes. Archive it instead.`
    //   400 `Confirmation is required.`
    //
    // It exists solely to clean up zero-byte orphans from creates the provider
    // never answered. IT CAN NEVER REACH LEARNER CONTENT, which is exactly what
    // the four conditions are for.
    VIDEO_PURGE: (id: string) =>
      `${baseUrl}/admin/bunny/videos/${encodeURIComponent(id)}/purge`,
  },
  // Admin-managed home-page counters (drives the nextjs home "Numbers" section).
  HOME_STATS: {
    LIST: `${baseUrl}/home-stats`,
    ADD: `${baseUrl}/home-stats/create`,
    PATCH: (id: string) => `${baseUrl}/home-stats/${id}`,
    DELETE: (id: string) => `${baseUrl}/home-stats/${id}`,
  },
  // Admin-managed About page — a single ordered list of section blocks
  // (drives the nextjs /about-us page). GET reads, PATCH replaces `sections`.
  ABOUT_PAGE: {
    GET: `${baseUrl}/about-page`,
    UPDATE: `${baseUrl}/about-page`,
  },
  // Admin notification campaigns (send to all users or a specific user).
  // A "campaign" is keyed by batchId; edit/delete act on the whole send.
  NOTIFICATIONS: {
    LIST: `${baseUrl}/notifications/admin`,
    ADD: `${baseUrl}/notifications/admin`,
    PATCH: (batchId: string) => `${baseUrl}/notifications/admin/${batchId}`,
    DELETE: (batchId: string) => `${baseUrl}/notifications/admin/${batchId}`,
    RECIPIENTS: (batchId: string) => `${baseUrl}/notifications/admin/${batchId}/recipients`,
  },
  TRACKS: {
    LIST: `${baseUrl}/tracks`,
    PAGINATED: `${baseUrl}/tracks/list`,
    ADD: `${baseUrl}/tracks/create`,
    PATCH: (id: string) => `${baseUrl}/tracks/${id}`,
    ACTIVE: `${baseUrl}/tracks/active`,
  },
  COURSES: {
    LIST: `${baseUrl}/courses`,
    PAGINATED: `${baseUrl}/courses/list`,
    ADD: `${baseUrl}/courses/create`,
    PATCH: (id: string) => `${baseUrl}/courses/${id}`,
    BY_ID: (id: string) => `${baseUrl}/courses/${id}`,
  },
  ROLES: {
    LIST: `${baseUrl}/roles`,
    ADD: `${baseUrl}/roles/create`,
    PATCH: (id: string) => `${baseUrl}/roles/${id}`,
  },
  PERMISSIONS: {
    LIST: `${baseUrl}/permissions`,
  },
  // Content management (ported from the live `dev` branch): blogs + SEO pages.
  BLOGS: {
    LIST: `${baseUrl}/blogs`,
    ADD: `${baseUrl}/blogs/create`,
    PATCH: (id: string) => `${baseUrl}/blogs/${id}`,
    BY_ID: (id: string) => `${baseUrl}/blogs/${id}`,
    DELETE: (id: string) => `${baseUrl}/blogs/${id}`,
  },
  // Admin-managed home-page "Success Stories" (student testimonials).
  SUCCESS_STORIES: {
    LIST: `${baseUrl}/success-stories`,
    ADD: `${baseUrl}/success-stories/create`,
    PATCH: (id: string) => `${baseUrl}/success-stories/${id}`,
    BY_ID: (id: string) => `${baseUrl}/success-stories/${id}`,
    DELETE: (id: string) => `${baseUrl}/success-stories/${id}`,
  },
  // Admin-managed items appended to the Company Profile page sections
  // (partner | diploma | company | story).
  COMPANY_EXTRAS: {
    LIST: `${baseUrl}/company-extras`,
    ADD: `${baseUrl}/company-extras/create`,
    PATCH: (id: string) => `${baseUrl}/company-extras/${id}`,
    BY_ID: (id: string) => `${baseUrl}/company-extras/${id}`,
    DELETE: (id: string) => `${baseUrl}/company-extras/${id}`,
  },
  SEO_PAGES: {
    LIST: `${baseUrl}/seoPages`,
    ADD: `${baseUrl}/seoPages/create`,
    PATCH: (id: string) => `${baseUrl}/seoPages/${id}`,
    BY_ID: (id: string) => `${baseUrl}/seoPages/${id}`,
    DELETE: (id: string) => `${baseUrl}/seoPages/${id}`,
  },
  // Reusable events with embedded lectures (drives the nextjs Events pages).
  EVENTS: {
    LIST: `${baseUrl}/events`,
    // Lightweight program list (no embedded lectures) for pickers/reports —
    // far cheaper than LIST, which ships every lecture's full quiz.
    SUMMARY: `${baseUrl}/events/summary`,
    ADD: `${baseUrl}/events/create`,
    PATCH: (id: string) => `${baseUrl}/events/${id}`,
    BY_ID: (id: string) => `${baseUrl}/events/${id}`,
    DELETE: (id: string) => `${baseUrl}/events/${id}`,
  },
  UPLOAD: {
    MEDIA: `${baseUrl}/files/media`,
    HTML: `${baseUrl}/files/html`,
    ANY: `${baseUrl}/files/any`,
    DOCUMENT: `${baseUrl}/files/document`,
    COURSE_DOCUMENT: `${baseUrl}/files/upload-course-document`,
    // Coursework/material upload. The ONLY endpoint whose mime allowlist accepts
    // .pptx/.docx. Response is NOT { data }-wrapped (FilesController applies only
    // LoggerInterceptor) — read res.data?.data ?? res.data, then .url.
    ASSIGNMENT: `${baseUrl}/files/assignment`,
  },
  SUPPORT: {
    handleDelete: `${baseUrl}/Users/updateUserPointsOnDelete`,
  },
  SCREEN: {
    getScreen: `${baseUrl}/customLoggers`,
  },
};
